import { useContext, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import * as client from "../../../client";
import Fuse, { FuseResult } from "fuse.js";
import { ThemeContext } from "../../../themes/Theme";
import { AutoComplete } from "primereact/autocomplete";
import { useAddressesForMap } from "../data";
import style from "./Search.module.css";
import { Tooltip } from "primereact/tooltip";
import { LOB } from "../../../api/routes";

export type IndexedData = {
  policy: string;
  named_insured: string;
  address: string;
  county: string | undefined;
};

export const useAddressSearcher = (addresses: client.SpatialKeyAddress[]) => {
  return useMemo(
    () =>
      new Fuse(
        addresses.map((a): IndexedData => {
          return {
            policy: a["Policy Number"],
            named_insured: a["Named Insured"],
            address: [a["Street 1"], a["Street 2"], a.City, a.State, a.Zip]
              .filter((x) => !!x)
              .join(" "),
            county: a["County"] ?? undefined,
          };
        }),
        {
          keys: [
            { name: "policy", weight: 1.0 },
            { name: "named_insured", weight: 1.0 },
            { name: "address", weight: 0.5 },
            { name: "county", weight: 0.5 },
          ],

          includeMatches: true,
          ignoreLocation: true,
          threshold: 0.1,
        }
      ),
    [addresses]
  );
};

export const highlightColor = (isLightMode: boolean) => {
  return isLightMode ? "var(--yellow-300)" : "#5e480344";
};

const KeyDescriptions: { [k in keyof IndexedData]: string } = {
  policy: "Policy",
  address: "Address",
  county: "County",
  named_insured: "Named Insured",
};

export const HighlightedItem = ({
  result,
}: {
  result: FuseResult<IndexedData>;
}) => {
  const item = result.item;
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const color = highlightColor(isLightMode);

  const highlight: [string, boolean][] = [];
  let key: keyof IndexedData | undefined = undefined;
  if (result.matches) {
    const m = result.matches[0];
    if (m.key) {
      key = m.key as keyof IndexedData;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const s = (item as any)[m.key];
      let pushed = 0;
      for (const [start, end] of m.indices) {
        highlight.push([s.substring(pushed, start), false]);
        highlight.push([s.substring(start, end + 1), true]);
        pushed = end + 1;
      }
      highlight.push([s.substring(pushed, s.length), false]);
    }
  }

  const description = key ? KeyDescriptions[key] : null;

  return (
    <div className={style.highlight}>
      <div className={style.highlightFirstRow}>
        <strong>{description}</strong>:{" "}
        {highlight.map(([s, highlight], i) => (
          <span
            key={i}
            style={highlight ? { backgroundColor: color } : undefined}
          >
            {s}
          </span>
        ))}
      </div>
      <span className={style.highlightSecondRow}>
        {item.policy}: {item.named_insured}
      </span>
    </div>
  );
};

export const AddressSearch = ({
  onPolicyChange,
  lobs,
}: {
  onPolicyChange: (policy: string) => void;
  lobs: LOB[];
}) => {
  const [suggestedPolicies, setSuggestedPolicies] = useState<
    FuseResult<IndexedData>[]
  >([]);
  const inforceAddresses = useAddressesForMap(lobs).data?.addresses;
  const search = useAddressSearcher(inforceAddresses ?? []);
  const [query, setQuery] = useState("");
  const tooltipId = useRef(`tooltip-${uuid()}`);
  return (
    <div className={style.autocompleteWrapper}>
      <span>Policy</span>
      <Tooltip target={`#${tooltipId.current}`} />
      <AutoComplete
        className={style.autocomplete}
        scrollHeight="400px"
        field="item.policy"
        value={query}
        onChange={(e) => setQuery(e.value)}
        onSelect={(e) => {
          const val: FuseResult<IndexedData> = e.value;
          onPolicyChange(val.item.policy);
        }}
        suggestions={suggestedPolicies}
        itemTemplate={(result) => <HighlightedItem result={result} />}
        completeMethod={(e) => {
          const rawResults = search.search(e.query);
          const results: FuseResult<IndexedData>[] = [];
          const seenPolicies = new Set();
          for (const result of rawResults) {
            const policy = result.item.policy;
            if (!seenPolicies.has(policy)) {
              seenPolicies.add(policy);
              results.push(result);
            }
          }

          setSuggestedPolicies(results);
        }}
        dropdown
        forceSelection
        id={tooltipId.current}
        tooltip="Search by policy number, named insured, or address."
        tooltipOptions={{ position: "left", showDelay: 500 }}
      />
    </div>
  );
};
